import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import productService, {
  Product,
  CreateProductDto,
  UpdateProductDto,
  ProductFilters,
  PaginatedResponse
} from '@/services/productService'
import { authService } from '@/services/authService'

interface ProductState {
  products: Product[]
  currentProduct: Product | null
  loading: boolean
  error: string | null
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
  filters: ProductFilters

  // Actions
  setFilters: (filters: ProductFilters) => void
  fetchProducts: (filters?: ProductFilters) => Promise<void>
  fetchProductById: (id: string) => Promise<void>
  createProduct: (data: CreateProductDto) => Promise<Product>
  updateProduct: (id: string, data: UpdateProductDto) => Promise<Product>
  deleteProduct: (id: string) => Promise<void>
  clearError: () => void
  clearCurrentProduct: () => void
}

const useProductStore = create<ProductState>()(
  devtools(
    (set, get) => ({
      products: [],
      currentProduct: null,
      loading: false,
      error: null,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
      },
      filters: {
        page: 1,
        limit: 10
      },

      setFilters: (filters) => {
        set({ filters: { ...get().filters, ...filters } })
      },

      fetchProducts: async (filters = {}) => {
        try {
          console.log('[DEBUG] productStore.fetchProducts called with filters:', filters)
          set({ loading: true, error: null })
          const currentFilters = { ...get().filters, ...filters }
          console.log('[DEBUG] Calling productService.getAll with filters:', currentFilters)
          const response: PaginatedResponse<Product> = await productService.getAll(currentFilters)
          console.log('[DEBUG] productService.getAll response:', response)
          
          // Get current user info
          const isAdmin = authService.isAdmin()
          const merchantContext = authService.getMerchantContext()
          
          let filteredProducts = response.data
          let filteredTotal = response.total
          
          // If user is not admin and has merchant context, filter products by merchant_id
          if (!isAdmin && merchantContext?.merchantId) {
            const userMerchantId = merchantContext.merchantId
            filteredProducts = response.data.filter(product =>
              product.merchant_id === userMerchantId
            )
            filteredTotal = filteredProducts.length
          }
          
          set({
            products: filteredProducts,
            pagination: {
              page: currentFilters.page || 1,
              limit: currentFilters.limit || 10,
              total: filteredTotal,
              pages: Math.ceil(filteredTotal / (currentFilters.limit || 10))
            },
            filters: currentFilters,
            loading: false
          })
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors du chargement des produits',
            loading: false
          })
        }
      },

      fetchProductById: async (id: string) => {
        try {
          set({ loading: true, error: null })
          const product = await productService.getById(id)
          set({ currentProduct: product, loading: false })
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors du chargement du produit',
            loading: false
          })
        }
      },

      createProduct: async (data: CreateProductDto) => {
        try {
          set({ loading: true, error: null })
          const product = await productService.create(data)
          
          // Add to products list
          set((state) => ({
            products: [product, ...state.products],
            loading: false
          }))

          return product
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors de la création du produit',
            loading: false
          })
          throw error
        }
      },

      updateProduct: async (id: string, data: UpdateProductDto) => {
        try {
          set({ loading: true, error: null })
          const product = await productService.update(id, data)
          
          // Update in products list
          set((state) => ({
            products: state.products.map(p => 
              p.product_id === id ? product : p
            ),
            currentProduct: state.currentProduct?.product_id === id ? product : state.currentProduct,
            loading: false
          }))

          return product
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors de la mise à jour du produit',
            loading: false
          })
          throw error
        }
      },

      deleteProduct: async (id: string) => {
        try {
          set({ loading: true, error: null })
          await productService.delete(id)
          
          // Remove from products list
          set((state) => ({
            products: state.products.filter(p => p.product_id !== id),
            currentProduct: state.currentProduct?.product_id === id ? null : state.currentProduct,
            loading: false
          }))
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors de la suppression du produit',
            loading: false
          })
          throw error
        }
      },

      clearError: () => set({ error: null }),
      clearCurrentProduct: () => set({ currentProduct: null })
    }),
    { name: 'ProductStore' }
  )
)

export default useProductStore
